import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailThread,
  getEmailThreads, syncEmailThreads, getSyncStatus, setThreadClassification, deleteEmailThread, markThreadRead,
  ReplyClassification,
} from '../api/emailThreads';
import { CLASSIFICATION_LABELS, CLASSIFICATION_ORDER } from '../components/mandates/classificationLabels';
import { ReplyDialog } from '../components/communication/ReplyDialog';

/**
 * Kommunikations-Übersicht als Baumstruktur:
 * Verkaufsmandat → Investor (Firma) → Threads (sent + received, neueste oben).
 * Abholen per „Alle Antworten abholen"-Button (IMAP-Sync, fire-and-forget mit Polling).
 */
export function RepliesPage() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  // expanded: true/false = explizit getoggelt; undefined = default-Verhalten (offen nur bei ungelesenen)
  const [expanded, setExpanded] = useState<Record<string, boolean | undefined>>({});
  const [expandedBody, setExpandedBody] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | ReplyClassification>('all');
  const [replyTo, setReplyTo] = useState<EmailThread | null>(null);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const r = await getEmailThreads({});
      setThreads(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadThreads(); }, []);

  const handleSync = async () => {
    setSyncBusy(true);
    setSyncMsg('Sync läuft im Hintergrund …');
    try {
      const r = await syncEmailThreads();
      if (r.alreadyRunning) setSyncMsg('Sync läuft bereits …');
      const startedAt = Date.now();
      const poll = async (): Promise<void> => {
        const status = await getSyncStatus();
        if (status.running && Date.now() - startedAt < 180_000) {
          setSyncMsg(`Sync läuft … ${status.imported} neu, ${status.skipped} bereits vorhanden`);
          setTimeout(poll, 3000);
          return;
        }
        if (status.error) setSyncMsg(`Fehler: ${status.error}`);
        else setSyncMsg(`Fertig: ${status.imported} neu, ${status.skipped} bereits vorhanden`);
        setSyncBusy(false);
        await loadThreads();
      };
      setTimeout(poll, 1500);
    } catch (e) {
      setSyncMsg(`Fehler: ${(e as Error).message}`);
      setSyncBusy(false);
    }
  };

  const handleClassify = async (id: string, value: ReplyClassification | null) => {
    setThreads(p => p.map(t => t.id === id ? { ...t, classification: value, classification_manual: true } : t));
    try { await setThreadClassification(id, value); }
    catch { await loadThreads(); }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Diesen Eintrag wirklich aus der Kommunikation löschen?')) return;
    const prev = threads;
    setThreads(p => p.filter(t => t.id !== id));
    try { await deleteEmailThread(id); }
    catch { setThreads(prev); }
  };

  // Baumdaten aufbauen — Mandat → Investor (Firma) → Threads
  const tree = useMemo(() => {
    const byMandate = new Map<string, {
      mandateId: string | null;
      mandateTitle: string;
      companies: Map<string, { companyId: string | null; companyName: string; threads: EmailThread[] }>;
    }>();

    const filtered = threads.filter(t => {
      if (filter === 'all') return true;
      if (filter === 'unread') return t.direction === 'received' && !t.is_read;
      return t.classification === filter;
    });

    for (const t of filtered) {
      const mKey = t.mandate_id ?? '__none__';
      const mTitle = t.mandate_title ?? (t.mandate_id ? '(Mandat unbekannt)' : 'Ohne Mandat');
      if (!byMandate.has(mKey)) {
        byMandate.set(mKey, { mandateId: t.mandate_id, mandateTitle: mTitle, companies: new Map() });
      }
      const mEntry = byMandate.get(mKey)!;

      const cKey = t.company_id ?? '__none__';
      const cName = t.company_display_name ?? (t.company_id ? '(Firma unbekannt)' : 'Ohne Firma');
      if (!mEntry.companies.has(cKey)) {
        mEntry.companies.set(cKey, { companyId: t.company_id, companyName: cName, threads: [] });
      }
      mEntry.companies.get(cKey)!.threads.push(t);
    }

    // In jeder Company: neueste oben
    for (const [, m] of byMandate) {
      for (const [, c] of m.companies) {
        c.threads.sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());
      }
    }

    // Mandate sortieren: jüngster Thread zuerst
    const mandateList = Array.from(byMandate.values()).sort((a, b) => {
      const aMax = Math.max(0, ...Array.from(a.companies.values()).flatMap(c => c.threads.map(t => new Date(t.occurred_at).getTime())));
      const bMax = Math.max(0, ...Array.from(b.companies.values()).flatMap(c => c.threads.map(t => new Date(t.occurred_at).getTime())));
      return bMax - aMax;
    });
    for (const m of mandateList) {
      m.companies = new Map(Array.from(m.companies.entries()).sort(([, a], [, b]) => {
        const aMax = Math.max(0, ...a.threads.map(t => new Date(t.occurred_at).getTime()));
        const bMax = Math.max(0, ...b.threads.map(t => new Date(t.occurred_at).getTime()));
        return bMax - aMax;
      }));
    }

    return mandateList;
  }, [threads, filter]);

  const toggle = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const goToMatch = (mandateId: string | null, companyId: string | null) => {
    if (!mandateId) return;
    const q = new URLSearchParams();
    q.set('tab', 'investoren');
    if (companyId) q.set('openInvestor', companyId);
    navigate(`/verkaufsmandate/${mandateId}?${q.toString()}`);
  };

  const unreadCount = threads.filter(t => t.direction === 'received' && !t.is_read).length;

  return (
    <div style={{ padding: '24px 28px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 600, margin: 0 }}>Kommunikation</h1>
        <div style={{ flex: 1 }} />
        <button onClick={handleSync} disabled={syncBusy}
          style={{
            padding: '8px 16px', background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 'var(--radius)',
            fontSize: '13px', fontWeight: 500,
            cursor: syncBusy ? 'wait' : 'pointer', opacity: syncBusy ? 0.7 : 1,
          }}>
          {syncBusy ? 'Sync läuft …' : 'Alle Antworten abholen'}
        </button>
      </div>

      {syncMsg && (
        <div style={{ padding: '10px 14px', marginBottom: '14px',
          background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--text2)' }}>
          {syncMsg}
        </div>
      )}

      {/* Filter-Chips */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '18px' }}>
        {([
          { key: 'all' as const,    label: 'Alle' },
          { key: 'unread' as const, label: `Ungelesen${unreadCount ? ` (${unreadCount})` : ''}` },
          ...CLASSIFICATION_ORDER.map(k => ({ key: k, label: CLASSIFICATION_LABELS[k].label })),
        ]).map(opt => {
          const active = filter === opt.key;
          return (
            <button key={opt.key} onClick={() => setFilter(opt.key)}
              style={{
                padding: '5px 11px', fontSize: '12px', borderRadius: '14px',
                border: '1px solid var(--border2)',
                background: active ? 'var(--accent)' : 'var(--surface)',
                color: active ? '#fff' : 'var(--text2)',
                cursor: 'pointer',
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text3)' }}>Laden …</div>
      ) : tree.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px',
          background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
          Keine Kommunikation in dieser Ansicht.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {tree.map(mandate => {
            const mKey = mandate.mandateId ?? '__none__';
            const unreadInMandateCount = Array.from(mandate.companies.values())
              .flatMap(c => c.threads)
              .filter(t => t.direction === 'received' && !t.is_read).length;
            // Default: nur offen, wenn Mandat ungelesene Nachrichten hat
            const mOpen = expanded[`m:${mKey}`] ?? (unreadInMandateCount > 0);
            const totalThreads = Array.from(mandate.companies.values()).reduce((n, c) => n + c.threads.length, 0);
            const unreadInMandate = unreadInMandateCount;

            return (
              <div key={mKey} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', overflow: 'hidden',
              }}>
                {/* Mandats-Kopf */}
                <div onClick={() => toggle(`m:${mKey}`)}
                  style={{
                    padding: '12px 16px', background: 'var(--surface2)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                    borderBottom: mOpen ? '1px solid var(--border)' : 'none',
                  }}>
                  <span style={{ fontSize: '12px', color: 'var(--text3)', width: '12px' }}>
                    {mOpen ? '▼' : '▶'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                    {mandate.mandateTitle}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text3)' }}>
                    {mandate.companies.size} Investor{mandate.companies.size !== 1 ? 'en' : ''} · {totalThreads} Nachrichten
                  </span>
                  {unreadInMandate > 0 && (
                    <span style={{
                      padding: '2px 7px', borderRadius: '10px',
                      fontSize: '10px', fontWeight: 700,
                      background: 'var(--accent)', color: '#fff',
                    }}>{unreadInMandate} neu</span>
                  )}
                </div>

                {/* Investoren */}
                {mOpen && Array.from(mandate.companies.values()).map(comp => {
                  const cKey = `${mKey}:${comp.companyId ?? '__none__'}`;
                  const cUnread = comp.threads.filter(t => t.direction === 'received' && !t.is_read).length;
                  // Default: nur offen, wenn Investor ungelesene Nachrichten hat
                  const cOpen = expanded[`c:${cKey}`] ?? (cUnread > 0);
                  return (
                    <div key={cKey}>
                      <div style={{
                        padding: '9px 16px 9px 36px',
                        borderBottom: cOpen ? '1px solid var(--border)' : '1px solid transparent',
                        display: 'flex', alignItems: 'center', gap: '10px',
                        background: 'var(--surface)',
                      }}>
                        <span onClick={() => toggle(`c:${cKey}`)}
                          style={{ fontSize: '11px', color: 'var(--text3)', cursor: 'pointer', width: '10px' }}>
                          {cOpen ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 500, flex: 1 }}>
                          {comp.companyName}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text3)' }}>
                          {comp.threads.length} Nachricht{comp.threads.length !== 1 ? 'en' : ''}
                        </span>
                        {cUnread > 0 && (
                          <span style={{
                            padding: '2px 6px', borderRadius: '8px',
                            fontSize: '10px', fontWeight: 700,
                            background: 'var(--accent)', color: '#fff',
                          }}>{cUnread} neu</span>
                        )}
                        {mandate.mandateId && comp.companyId && (
                          <button onClick={e => { e.stopPropagation(); goToMatch(mandate.mandateId, comp.companyId); }}
                            style={{
                              padding: '3px 9px', fontSize: '11px',
                              background: 'var(--surface2)', border: '1px solid var(--border2)',
                              borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text2)',
                            }}>
                            Zum Match →
                          </button>
                        )}
                      </div>

                      {/* Threads */}
                      {cOpen && comp.threads.map(t => {
                        const isSent = t.direction === 'sent';
                        const cls = t.classification ? CLASSIFICATION_LABELS[t.classification] : null;
                        const partner = isSent ? t.to_email : t.from_email;
                        const dateObj = new Date(t.occurred_at);
                        const unread = !isSent && !t.is_read;
                        return (
                          <div key={t.id}
                            onClick={async () => {
                              setExpandedBody(expandedBody === t.id ? null : t.id);
                              if (unread) {
                                setThreads(p => p.map(x => x.id === t.id ? { ...x, is_read: true } : x));
                                void markThreadRead(t.id).catch(() => {/* ignore */});
                              }
                            }}
                            style={{
                              padding: '10px 16px 10px 56px',
                              borderBottom: '1px solid var(--border)',
                              background: unread ? '#fffbf5' : 'var(--surface)',
                              cursor: 'pointer',
                              opacity: (!isSent && t.is_read) ? 0.75 : 1,
                            }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
                                  <span style={{
                                    fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    color: isSent ? 'var(--text3)' : 'var(--accent)',
                                  }}>
                                    {isSent ? '→ Gesendet' : '← Antwort'}
                                  </span>
                                  {unread && (
                                    <span style={{
                                      padding: '1px 6px', borderRadius: '8px',
                                      fontSize: '9px', fontWeight: 700,
                                      background: 'var(--accent)', color: '#fff',
                                    }}>Neu</span>
                                  )}
                                  <span style={{ fontSize: '12px', color: 'var(--text2)', fontFamily: 'DM Mono, monospace' }}>
                                    {partner}
                                  </span>
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text2)' }}>
                                  <strong>{t.subject}</strong>
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text3)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                    {dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                    {' · '}
                                    {dateObj.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <button onClick={e => { e.stopPropagation(); setReplyTo(t); }} title="Antworten"
                                    style={{ padding: '3px 9px', fontSize: '11px',
                                      background: 'var(--surface2)', border: '1px solid var(--border2)',
                                      borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text2)' }}>
                                    ↩ Antworten
                                  </button>
                                  <button onClick={e => handleDelete(t.id, e)} title="Löschen"
                                    style={{ background: 'none', border: 'none', padding: '0 4px', cursor: 'pointer',
                                      fontSize: '12px', color: 'var(--text3)' }}>×</button>
                                </div>
                                {cls && (
                                  <span style={{
                                    padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                                    background: cls.bg, color: cls.color,
                                  }}>
                                    {cls.icon} {cls.label}
                                  </span>
                                )}
                                {!isSent && (
                                  <select
                                    value={t.classification ?? ''}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => { e.stopPropagation(); void handleClassify(t.id, (e.target.value || null) as ReplyClassification | null); }}
                                    style={{
                                      padding: '1px 4px', fontSize: '10px',
                                      border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
                                      background: 'var(--surface)', cursor: 'pointer',
                                    }}>
                                    <option value="">— offen —</option>
                                    {CLASSIFICATION_ORDER.map(k => (
                                      <option key={k} value={k}>{CLASSIFICATION_LABELS[k].label}</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </div>
                            {expandedBody === t.id && t.body_text && (
                              <pre style={{
                                marginTop: '8px', padding: '10px 12px',
                                background: 'var(--surface2)', borderRadius: 'var(--radius)',
                                fontSize: '11px', color: 'var(--text2)', lineHeight: 1.6,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                border: '1px solid var(--border)', maxHeight: '260px', overflow: 'auto',
                              }}>
                                {t.body_text}
                              </pre>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <ReplyDialog thread={replyTo} onClose={() => setReplyTo(null)} onSent={loadThreads} />
    </div>
  );
}
